import { supabase } from "@/lib/supabase";
import { mapDocumentType } from "@/lib/utils/documentMapper";

export interface VerificationRequestStatus {
  exists: boolean;
  id?: string;
  status?: "pending" | "under_review" | "approved" | "rejected";
  is_complete?: boolean;
  submitted_at?: string | null;
  review_notes?: string | null;
  reviewNotes?: string; // Alias pour compatibilité
}

export interface DocumentValidationResult {
  isValid: boolean;
  missingDocuments: string[];
  uploadedDocuments: string[];
  errors: string[];
}

/**
 * Verification Service
 * SOLID: Single Responsibility - Toute la logique métier de vérification
 * KISS: Fonctions simples et claires
 */
export const verificationService = {
  /**
   * Check if user has an existing verification request
   * Returns the status and details if exists
   */
  async checkExistingVerificationRequest(
    userId: string,
    verificationType: "tenant" | "host"
  ): Promise<VerificationRequestStatus> {
    try {
      console.log("[verificationService] Checking verification request for:", {
        userId,
        verificationType,
      });

      // First, try to get the most recent request of the specific type
      const { data, error } = await supabase
        .from("verification_requests")
        .select("id, status, is_complete, submitted_at, review_notes")
        .eq("user_id", userId)
        .eq("verification_type", verificationType)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error("[verificationService] Error querying verification_requests:", error);
        console.error("[verificationService] Error details:", {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
        });
        // If table doesn't exist, return no request
        if (error.message?.includes("does not exist") || error.message?.includes("schema cache")) {
          console.log(
            "[verificationService] Table doesn't exist or schema cache issue, returning no request"
          );
          return { exists: false };
        }
        throw error;
      }

      if (!data) {
        console.log("[verificationService] No verification request found for:", {
          userId,
          verificationType,
        });

        // As a fallback, check if there are ANY verification requests for this user (to debug RLS issues)
        const { data: anyRequest, error: anyError } = await supabase
          .from("verification_requests")
          .select("id, verification_type, status")
          .eq("user_id", userId)
          .limit(1)
          .maybeSingle();

        if (anyError) {
          console.log(
            "[verificationService] Error checking for any verification requests (RLS check):",
            anyError
          );
        } else if (anyRequest) {
          console.log("[verificationService] Found verification request with different type:", {
            id: anyRequest.id,
            type: anyRequest.verification_type,
            status: anyRequest.status,
          });
        } else {
          console.log("[verificationService] No verification requests found for this user at all");
        }

        return { exists: false };
      }

      console.log("[verificationService] Found verification request:", {
        id: data.id,
        status: data.status,
        is_complete: data.is_complete,
        submitted_at: data.submitted_at,
      });

      return {
        exists: true,
        id: data.id,
        status: data.status as "pending" | "under_review" | "approved" | "rejected",
        is_complete: data.is_complete,
        submitted_at: data.submitted_at,
        review_notes: data.review_notes || undefined,
        reviewNotes: data.review_notes || undefined, // Alias pour compatibilité
      };
    } catch (error: any) {
      console.error("[verificationService] Error checking existing verification request:", error);
      console.error("[verificationService] Error stack:", error.stack);
      // Return no request on error to allow retry
      return { exists: false };
    }
  },

  /**
   * Validate that all required documents are uploaded and exist in database
   */
  async validateDocumentsForSubmission(
    userId: string,
    requiredDocumentTypes: string[]
  ): Promise<DocumentValidationResult> {
    const result: DocumentValidationResult = {
      isValid: false,
      missingDocuments: [],
      uploadedDocuments: [],
      errors: [],
    };

    try {
      if (import.meta.env.DEV) {
        console.log("[validateDocumentsForSubmission] Validating documents for user:", {
          userId,
          requiredDocumentTypes,
        });
      }

      // Get all user documents
      const { data: documents, error } = await supabase
        .from("documents")
        .select("id, type, file_url, status")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("[validateDocumentsForSubmission] Error fetching documents:", error);
        result.errors.push(`Erreur lors de la récupération des documents: ${error.message}`);
        return result;
      }

      if (import.meta.env.DEV) {
        console.log("[validateDocumentsForSubmission] Found documents:", documents?.length || 0);
      }

      // Map documents to their actual types
      const mappedDocuments = (documents || []).map((doc) => ({
        ...doc,
        mappedType: mapDocumentType(doc.type, doc.file_url || ""),
      }));

      if (import.meta.env.DEV) {
        console.log(
          "[validateDocumentsForSubmission] Mapped documents:",
          mappedDocuments.map((d) => ({
            dbType: d.type,
            mappedType: d.mappedType,
            status: d.status,
            fileName: d.file_url?.split("/").pop(),
          }))
        );
      }

      // Check each required document type
      const uploadedTypes = new Set<string>();

      for (const requiredType of requiredDocumentTypes) {
        // Chercher d'abord par mappedType exact
        let found = mappedDocuments.some(
          (doc) => doc.mappedType === requiredType && doc.status !== "rejected"
        );

        if (import.meta.env.DEV && !found) {
          console.log(
            `[validateDocumentsForSubmission] Document ${requiredType} not found by mappedType, trying fallbacks...`
          );
        }

        // Fallback: pour identity_card, chercher aussi "identity" (compatibilité)
        if (!found && requiredType === "identity_card") {
          found = mappedDocuments.some(
            (doc) =>
              (doc.mappedType === "identity" || doc.type === "identity") &&
              doc.status !== "rejected"
          );
          if (import.meta.env.DEV && found) {
            console.log(
              `[validateDocumentsForSubmission] Found ${requiredType} via identity fallback`
            );
          }
        }

        // Fallback: pour identity, chercher aussi "identity_card" (compatibilité)
        if (!found && requiredType === "identity") {
          found = mappedDocuments.some(
            (doc) =>
              (doc.mappedType === "identity_card" || doc.type === "identity_card") &&
              doc.status !== "rejected"
          );
          if (import.meta.env.DEV && found) {
            console.log(
              `[validateDocumentsForSubmission] Found ${requiredType} via identity_card fallback`
            );
          }
        }

        if (found) {
          uploadedTypes.add(requiredType);
          result.uploadedDocuments.push(requiredType);
          if (import.meta.env.DEV) {
            console.log(`[validateDocumentsForSubmission] ✅ Document ${requiredType} found`);
          }
        } else {
          result.missingDocuments.push(requiredType);
          if (import.meta.env.DEV) {
            console.log(`[validateDocumentsForSubmission] ❌ Document ${requiredType} missing`);
          }
        }
      }

      result.isValid = result.missingDocuments.length === 0;

      if (import.meta.env.DEV) {
        console.log("[validateDocumentsForSubmission] Validation result:", {
          isValid: result.isValid,
          uploaded: result.uploadedDocuments,
          missing: result.missingDocuments,
        });
      }

      return result;
    } catch (error: any) {
      result.errors.push(`Erreur de validation: ${error.message || "Erreur inconnue"}`);
      return result;
    }
  },

  /**
   * Create a new verification request
   * Automatically links existing documents to the request
   */
  async createVerificationRequest(
    userId: string,
    verificationType: "tenant" | "host",
    requiredDocuments: string[],
    completedDocuments: string[]
  ): Promise<{ id: string; success: boolean; error?: string }> {
    try {
      // 1. Create the verification request
      const { data: request, error } = await supabase
        .from("verification_requests")
        .insert({
          user_id: userId,
          verification_type: verificationType,
          required_documents: requiredDocuments,
          completed_documents: completedDocuments,
          is_complete: true,
          status: "under_review",
          submitted_at: new Date().toISOString(),
        })
        .select("id")
        .single();

      if (error) {
        return {
          id: "",
          success: false,
          error: error.message,
        };
      }

      // 2. Link existing documents to this request
      // Documents that match the verification_type and are not yet linked
      try {
        const { error: linkError } = await supabase
          .from("documents")
          .update({
            verification_request_id: request.id,
            verification_type: verificationType,
          })
          .eq("user_id", userId)
          .or(
            `verification_type.eq.${verificationType},verification_type.is.null`
          )
          .is("verification_request_id", null);

        if (linkError) {
          console.error(
            "[createVerificationRequest] Error linking documents:",
            linkError
          );
          // Don't fail the request creation if linking fails
          // The documents can be linked later via migration
        } else if (import.meta.env.DEV) {
          console.log(
            `[createVerificationRequest] Successfully linked documents to request ${request.id}`
          );
        }
      } catch (linkErr) {
        console.error(
          "[createVerificationRequest] Exception linking documents:",
          linkErr
        );
        // Continue even if linking fails
      }

      return {
        id: request.id,
        success: true,
      };
    } catch (error: any) {
      return {
        id: "",
        success: false,
        error: error.message || "Erreur lors de la création de la demande",
      };
    }
  },

  /**
   * Update an existing verification request
   */
  async updateVerificationRequest(
    requestId: string,
    completedDocuments: string[]
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from("verification_requests")
        .update({
          is_complete: true,
          completed_documents: completedDocuments,
          status: "under_review",
          submitted_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", requestId);

      if (error) {
        return {
          success: false,
          error: error.message,
        };
      }

      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || "Erreur lors de la mise à jour de la demande",
      };
    }
  },

  /**
   * Get user's documents with mapped types
   */
  async getUserDocuments(userId: string): Promise<
    Array<{
      id: string;
      type: string;
      mappedType: string;
      file_url: string;
      status: string;
      created_at: string;
    }>
  > {
    try {
      const { data, error } = await supabase
        .from("documents")
        .select(
          "id, type, file_url, status, rejection_reason, verified_at, verified_by, created_at"
        )
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (error) {
        throw error;
      }

      return (data || []).map((doc) => ({
        id: doc.id,
        type: doc.type,
        mappedType: mapDocumentType(doc.type, doc.file_url || ""),
        file_url: doc.file_url,
        status: doc.status,
        created_at: doc.created_at,
      }));
    } catch (error: any) {
      console.error("Error fetching user documents:", error);
      return [];
    }
  },

  /**
   * Resubmit verification request
   * Updates existing verification request to "under_review" status
   * SOLID: Single Responsibility - Handles resubmission only
   * KISS: Simple update of status and submitted_at
   */
  async resubmitVerificationRequest(
    userId: string,
    verificationType: "tenant" | "host"
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // 1. Check if verification request exists and get required documents
      const { data: existingRequest, error: fetchError } = await supabase
        .from("verification_requests")
        .select("id, required_documents")
        .eq("user_id", userId)
        .eq("verification_type", verificationType)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (fetchError) {
        return {
          success: false,
          error: fetchError.message,
        };
      }

      if (!existingRequest || !existingRequest.id) {
        return {
          success: false,
          error: "Aucune demande de vérification trouvée. Veuillez d'abord soumettre vos documents.",
        };
      }

      // 2. Validate that all required documents are present
      const requiredDocTypes = existingRequest.required_documents || [];

      if (requiredDocTypes.length === 0) {
        // Fallback: use default required documents if not in request
        const defaultRequired =
          verificationType === "tenant"
            ? ["identity_card", "driver_license", "proof_of_address"]
            : [
              "identity",
              "vehicle_registration",
              "insurance",
              "technical_inspection",
              "business_premises_photo",
              "trade_register",
            ];
        requiredDocTypes.push(...defaultRequired);
      }

      const validation = await this.validateDocumentsForSubmission(userId, requiredDocTypes);

      if (!validation.isValid) {
        return {
          success: false,
          error: `Documents manquants: ${validation.missingDocuments.join(", ")}`,
        };
      }

      // 2.5 Link newly uploaded documents (re-link)
      // This ensures that replaced documents are linked to the request
      try {
        const { error: linkError } = await supabase
          .from("documents")
          .update({
            verification_request_id: existingRequest.id,
            verification_type: verificationType,
          })
          .eq("user_id", userId)
          .in("type", requiredDocTypes)
          .neq("status", "rejected"); // Don't link rejected documents (should be new ones)

        if (linkError) {
          console.error("[resubmitVerificationRequest] Error linking documents:", linkError);
          // We continue, as this might be non-critical if query is complex
        } else if (import.meta.env.DEV) {
          console.log(`[resubmitVerificationRequest] Linked documents to request ${existingRequest.id}`);
        }
      } catch (linkErr) {
        console.error("[resubmitVerificationRequest] Exception linking documents:", linkErr);
      }

      // 3. Update verification request
      const { error } = await supabase
        .from("verification_requests")
        .update({
          status: "under_review",
          submitted_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          completed_documents: validation.uploadedDocuments, // Update completed documents list
          review_notes: null, // Reset review notes
        })
        .eq("id", existingRequest.id);

      if (error) {
        return {
          success: false,
          error: error.message,
        };
      }

      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || "Erreur lors de la re-soumission de la vérification",
      };
    }
  },
};
